import type { UserPreferences } from '@court-sync/shared';
import type { TourStep } from '@/components/ui/Tour';
import { getCampBulkWizardTourSteps } from './camp-bulk-wizard-tour-steps';

export const CAMP_BULK_WIZARD_TOUR_BASE = 'academy-camps-bulk-wizard';

const TARGET_POLL_MS = 50;
const TARGET_TIMEOUT_MS = 2500;
const STEP_RENDER_BUFFER_MS = 80;

function findTourTarget(selectorId: string): Element | null {
  return (
    document.getElementById(selectorId) ?? document.querySelector(`[data-tour="${selectorId}"]`)
  );
}

export function getCampBulkWizardStepCompletionKey(wizardStep: number): string {
  return `${CAMP_BULK_WIZARD_TOUR_BASE}:${wizardStep}`;
}

export function isCampBulkWizardStepTourComplete(
  preferences: UserPreferences | null | undefined,
  wizardStep: number,
): boolean {
  return preferences?.completedPageTours?.[getCampBulkWizardStepCompletionKey(wizardStep)] === true;
}

function areWizardStepTargetsReady(steps: TourStep[]): boolean {
  if (steps.length === 0) return false;
  return steps.every((step) => findTourTarget(step.selectorId) != null);
}

/** Wait for step content to mount, then start the first-time tour for this wizard step. */
export function scheduleCampBulkWizardStepTour(
  startTour: (completionKey: string, steps: TourStep[]) => void,
  wizardStep: number,
  preferences: UserPreferences | undefined,
): () => void {
  const completionKey = getCampBulkWizardStepCompletionKey(wizardStep);
  if (isCampBulkWizardStepTourComplete(preferences, wizardStep)) {
    return () => {};
  }

  const steps = getCampBulkWizardTourSteps(wizardStep);
  if (steps.length === 0) {
    return () => {};
  }

  let cancelled = false;
  let pollTimer: number | undefined;
  let startTimer: number | undefined;
  const pollStartedAt = Date.now();

  const attemptStart = () => {
    if (cancelled) return;
    if (areWizardStepTargetsReady(steps) || Date.now() - pollStartedAt >= TARGET_TIMEOUT_MS) {
      startTour(completionKey, steps);
      return;
    }
    pollTimer = window.setTimeout(attemptStart, TARGET_POLL_MS);
  };

  startTimer = window.setTimeout(attemptStart, STEP_RENDER_BUFFER_MS);

  return () => {
    cancelled = true;
    if (startTimer !== undefined) window.clearTimeout(startTimer);
    if (pollTimer !== undefined) window.clearTimeout(pollTimer);
  };
}
